import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';

/**
 * ONE state machine for every attachment image in the app. Transport is injected.
 *
 * Returns { imageState, retryImage, reportLoaded, reportError } where imageState is
 *   { [file_id]: { status: 'loading' | 'ready' | 'failed' | 'gone', url?, reason? } }
 *
 * THE PROPERTY IT EXISTS TO HOLD: the reader always learns a screenshot exists. Two
 * separate defects were both instances of breaking it — the file-request panels had
 * a bare `catch {}` and rendered nothing at all, and chat latched a single failure
 * into a permanent "(unavailable)" with no retry. Silence and a stuck error are the
 * same bug wearing different clothes, so they now share one implementation.
 *
 * ── TWO TRANSPORTS, AND THEY CANNOT BE UNIFIED ────────────────────────────────
 *
 *   transport: 'blob'    file-request images. Fetched with CORS and handed to the
 *                        <img> as a blob: URL. Required because that scope STREAMS
 *                        the bytes, so the app's own response carries them together
 *                        with the global `Cross-Origin-Resource-Policy: same-site`
 *                        header and Chrome discards a plain <img src> before paint.
 *                        CORP does not apply to CORS fetches; a blob: URL is
 *                        same-origin by construction.
 *
 *   transport: 'direct'  chat attachments. The <img> points straight at the serve
 *                        endpoint. Required because that scope 302s to R2: a CORS
 *                        fetch would follow the redirect cross-origin, the browser
 *                        would send `Origin: null`, and R2 returns no
 *                        Access-Control-Allow-Origin for null.
 *
 * Each transport is the only one that works for its scope. Making them the same
 * needs a SERVER change (streaming chat attachments), which is a bandwidth decision
 * — see the comment at the `project_file_request` branch of /storage/serve — and is
 * deliberately not made here.
 *
 * ── WHAT EACH TRANSPORT CAN KNOW ──────────────────────────────────────────────
 * 'blob' reads the HTTP status, so it can tell 410 (purged) from 404/5xx/network and
 * report `gone` honestly. 'direct' cannot: an <img> reports load or error and
 * nothing else. Chat therefore never shows `gone` — every failure is `failed` with a
 * retry offered. That is a real limitation of the transport, not an oversight.
 *
 * ── RETRY POLICY ──────────────────────────────────────────────────────────────
 *   failed   2 automatic retries, ~500ms then ~1500ms
 *   410      ONE retry after ~1500ms, THEN `gone`   ('blob' only)
 *   403      terminal immediately, no automatic retry
 *
 * The 410 retry is not padding. `confirm-upload` strips EXIF and REWRITES the
 * object, so a file asked for moments later was written seconds ago and a
 * `head_object` that has not caught up returns 410. The delay is the LONG one on
 * purpose: a read-after-write visibility gap resolves with time, so re-asking
 * immediately would likely 410 again and print "No longer available" about a file
 * that is about to appear — a false statement, which is worse than the silence this
 * replaces because a wrong answer gets acted on.
 */

const FAILED_RETRY_MS = [500, 1500];
const GONE_RETRY_MS = 1500;

export function useAttachmentImages(items, { transport = 'blob', endpoint = '' } = {}) {
  const [imageState, setImageState] = useState({});

  /* ATTEMPT COUNTERS LIVE IN A REF, KEYED BY file_id — deliberately not in state.
   * Chat threads and the project drawer both poll. If counters were state, any
   * re-render that re-derived an identical id set could reset them and re-fire the
   * whole ladder, turning one transient failure into a request storm. A ref is
   * untouched by rendering and is cleared in exactly one place: the [ids] effect,
   * which only runs when the id STRING changes. */
  const attempts = useRef({});
  const timers = useRef([]);
  const created = useRef([]);
  const cancelled = useRef(false);

  const ids = (items || []).map(i => i.file_id).join(',');

  /* Direct-transport URL. The cache-buster is added ONLY from attempt 1 onward, so
   * the success path — which is every image that works — is a clean, cacheable URL.
   * Busting on the first request would defeat legitimate caching for everyone to
   * serve the rare retry. */
  const directUrl = (id, attempt) =>
    attempt > 0 ? `${endpoint}/${id}?r=${attempt}` : `${endpoint}/${id}`;

  const fetchRef = useRef();
  fetchRef.current = async (id) => {
    if (cancelled.current) return;

    if (transport === 'direct') {
      // Nothing to fetch: hand the <img> a URL and wait for it to tell us.
      const a = attempts.current[id] || { failed: 0, gone: 0 };
      setImageState(prev => ({ ...prev, [id]: { status: 'loading', url: directUrl(id, a.failed) } }));
      return;
    }

    setImageState(prev => ({ ...prev, [id]: { ...(prev[id] || {}), status: 'loading' } }));
    try {
      const res = await api.get(`/storage/serve/${id}`, { responseType: 'blob' });
      if (cancelled.current) return;
      const url = URL.createObjectURL(res.data);
      created.current.push(url);
      setImageState(prev => ({ ...prev, [id]: { status: 'ready', url } }));
    } catch (e) {
      if (cancelled.current) return;
      handleFailure(id, e?.response?.status);
    }
  };

  const handleFailure = (id, code) => {
    const a = attempts.current[id] || { failed: 0, gone: 0 };

    if (code === 403) {                       // a permission answer is stable
      setImageState(prev => ({ ...prev, [id]: { status: 'failed', reason: 'denied' } }));
      return;
    }
    if (code === 410) {
      if (a.gone < 1) {
        a.gone += 1; attempts.current[id] = a;
        timers.current.push(setTimeout(() => fetchRef.current(id), GONE_RETRY_MS));
        return;
      }
      setImageState(prev => ({ ...prev, [id]: { status: 'gone', reason: 'purged' } }));
      return;
    }
    if (a.failed < FAILED_RETRY_MS.length) {
      const delay = FAILED_RETRY_MS[a.failed];
      a.failed += 1; attempts.current[id] = a;
      timers.current.push(setTimeout(() => fetchRef.current(id), delay));
      return;
    }
    setImageState(prev => ({
      ...prev, [id]: { status: 'failed', reason: code ? 'server' : 'network' },
    }));
  };

  /** The <img> painted. For 'direct' this is the only success signal there is. */
  const reportLoaded = (id) => {
    setImageState(prev => (prev[id]?.status === 'ready' ? prev
      : { ...prev, [id]: { ...(prev[id] || {}), status: 'ready' } }));
  };

  /** The <img> failed. For 'direct' this is the only failure signal there is, and it
   *  carries no status code — hence `undefined`, which routes to the failed ladder.
   *  For 'blob' the bytes already arrived, so this is a DECODE failure: retrying the
   *  fetch would return identical bytes, so it is terminal. */
  const reportError = (id) => {
    if (transport === 'blob') {
      setImageState(prev => ({ ...prev, [id]: { status: 'failed', reason: 'decode' } }));
      return;
    }
    handleFailure(id, undefined);
  };

  useEffect(() => {
    cancelled.current = false;
    attempts.current = {};        // the ONLY reset point — a genuinely new id set
    setImageState({});
    (ids ? ids.split(',') : []).forEach(id => fetchRef.current(id));

    // The drawer and the chat pane both mount and unmount repeatedly, so an
    // un-revoked object URL is a real leak for the life of the tab, and a pending
    // timer would set state on an unmounted component.
    return () => {
      cancelled.current = true;
      timers.current.forEach(clearTimeout);
      timers.current = [];
      created.current.forEach(u => URL.revokeObjectURL(u));
      created.current = [];
      setImageState({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, transport, endpoint]);

  /** Manual "Try again". Clears this file's counters so the full ladder is available
   *  again — a person asking for a retry is new information. */
  const retryImage = (id) => {
    attempts.current[id] = { failed: 0, gone: 0 };
    if (transport === 'direct') {
      // Force a fresh load even though the URL would otherwise be identical.
      const a = { failed: 1, gone: 0 };
      attempts.current[id] = a;
      setImageState(prev => ({ ...prev, [id]: { status: 'loading', url: directUrl(id, Date.now() % 100000) } }));
      return;
    }
    fetchRef.current(id);
  };

  return { imageState, retryImage, reportLoaded, reportError };
}

export default useAttachmentImages;
